import { useEffect, useState } from "react";
import { errText, postJson } from "@/lib/api";
import { Credit } from "@/components/dash/Credit";

const Side = () => (
  <section className="login-side">
    <img className="brand-logo" src="/brand/hdfc-bank-logo.svg" alt="HDFC Bank" width="224" height="44" />
    <div>
      <div className="eyebrow">HDFC Bank Parivartan · Impact assessment</div>
      <h1>Impact Dashboard</h1>
    </div>
    <p className="login-lead">OECD-DAC project scores, activity-wise responses and SurveyCTO submission status for every research agency, refreshed daily.</p>
    <ul className="login-list">
      <li><span>01</span>Project score, indicator profile and weightage</li>
      <li><span>02</span>Activity responses with rating distributions</li>
      <li><span>03</span>Submission status and data folders</li>
    </ul>
  </section>
);

function useCountdown() {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (left <= 0) return undefined;
    const t = setTimeout(() => setLeft(l => l - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);
  return [left, setLeft];
}

function EmailStep({ email, setEmail, onSent }) {
  const [busy, setBusy] = useState(false), [err, setErr] = useState("");
  const submit = async e => {
    e.preventDefault();
    const v = email.trim().toLowerCase();
    if (!v) return;
    setBusy(true); setErr("");
    try {
      const r = await postJson("/api/auth/request-otp", { email: v }), d = await r.json();
      if (r.ok) onSent(v, d.cooldown || 60);
      else if (r.status === 429) onSent(v, (d.detail && d.detail.retry_after) || 60, errText(d));
      else setErr(errText(d));
    } catch (x) { setErr("Network error. Check your connection and try again."); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} noValidate data-testid="login-email-form">
      {err ? <div className="lg-msg err" role="alert" data-testid="login-error">{err}</div> : null}
      <div className="lg-field"><label htmlFor="lg-email">Work email</label>
        <input id="lg-email" data-testid="login-email-input" type="email" autoComplete="email" placeholder="name@organisation.com" value={email} onChange={e => setEmail(e.target.value)} autoFocus /></div>
      <button className="lg-primary" type="submit" disabled={busy || !email.trim()} data-testid="login-send-code-btn">{busy ? "Sending…" : "Send code"}</button>
    </form>
  );
}

function CodeStep({ email, cooldown, info, onBack, onDone }) {
  const [code, setCode] = useState(""), [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(info ? { t: "err", s: info } : { t: "ok", s: `We sent a 6-digit code to ${email}. It expires in 10 minutes.` });
  const [left, setLeft] = useCountdown();
  useEffect(() => setLeft(cooldown), [cooldown, setLeft]);
  useEffect(() => {
    // Dev-only: when the backend is in TEST_MODE the OTP is exposed at this endpoint
    // (404 otherwise). Prefill the field and auto-sign-in so the UI "just works" locally.
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/auth/test/otp?email=${encodeURIComponent(email)}`, { credentials: "include" });
        if (!r.ok || cancelled) return;
        const d = await r.json();
        if (!d.code || cancelled) return;
        setCode(d.code);
        setMsg({ t: "ok", s: "TEST_MODE: code auto-filled — signing you in…" });
        const v = await postJson("/api/auth/verify-otp", { email, code: d.code });
        const vd = await v.json();
        if (!cancelled && v.ok) onDone(vd);
      } catch (_) { /* non-test-mode: fall through to manual entry */ }
    })();
    return () => { cancelled = true; };
  }, [email, onDone]);
  const verify = async e => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) { setMsg({ t: "err", s: "Enter the 6-digit code from the email." }); return; }
    setBusy(true);
    try {
      const r = await postJson("/api/auth/verify-otp", { email, code }), d = await r.json();
      if (r.ok) { onDone(d); return; }
      setMsg({ t: "err", s: errText(d) }); setCode("");
    } catch (x) { setMsg({ t: "err", s: "Network error. Check your connection and try again." }); }
    setBusy(false);
  };
  const resend = async () => {
    const r = await postJson("/api/auth/request-otp", { email }), d = await r.json();
    if (r.ok) { setMsg({ t: "ok", s: `A new code is on its way to ${email}.` }); setLeft(d.cooldown || 60); setCode(""); }
    else { setMsg({ t: "err", s: errText(d) }); if (r.status === 429) setLeft((d.detail && d.detail.retry_after) || 60); }
  };
  return (
    <form onSubmit={verify} noValidate data-testid="login-code-form">
      <div className={`lg-msg ${msg.t}`} role={msg.t === "err" ? "alert" : "status"} data-testid={msg.t === "err" ? "login-error" : "login-info"}>{msg.s}</div>
      <div className="lg-field"><label htmlFor="lg-code">Sign-in code</label>
        <input id="lg-code" className="code" data-testid="login-code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="••••••" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ""))} autoFocus /></div>
      <button className="lg-primary" type="submit" disabled={busy || code.length !== 6} data-testid="login-verify-btn">{busy ? "Checking…" : "Sign in"}</button>
      <div className="lg-row">
        <button type="button" className="lg-link" onClick={onBack} data-testid="login-change-email-btn">Use a different email</button>
        <button type="button" className="lg-link" onClick={resend} disabled={left > 0} data-testid="login-resend-btn">{left > 0 ? `Resend code (${left}s)` : "Resend code"}</button>
      </div>
    </form>
  );
}

export default function Login({ onDone }) {
  const [email, setEmail] = useState(""), [sent, setSent] = useState(null);
  return (
    <div className="login" data-testid="login-page">
      <Side />
      <section className="login-main">
        <div className="login-card">
          <div className="kpi-label">Sign in</div>
          <h2>Access the dashboard</h2>
          <p className="sub">{sent ? "Check your inbox and enter the code." : "Enter your work email. We'll email you a one-time code; there is no password."}</p>
          {sent ? <CodeStep key={sent.n} email={sent.email} cooldown={sent.cooldown} info={sent.info} onBack={() => setSent(null)} onDone={onDone} />
            : <EmailStep email={email} setEmail={setEmail} onSent={(e, cooldown, info) => setSent({ email: e, cooldown, info, n: Date.now() })} />}
          <p className="lg-note">Access is limited to authorised users. Each sign-in lasts 7 days on this device.</p>
        </div>
        <Credit />
      </section>
    </div>
  );
}
